import {Injectable} from '@angular/core';
import {AppService} from '../../../app-common/services/app/app.service';
import {ApiService} from '../../../app-common/services/server/api.service';
import {AppStateService, AppWindowState} from '../../../app-common/services/appState/app-state.service';
import {TenantDto} from '../../data/tenantDto';
import buildNumber from '../../../../../buildNumber.json';
import {TabbingService} from '../../../tabbing/services/tabbing.service';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {CreateOrganizationRequest} from '../../data/createOrganizationRequest';
import {Tenant} from '../../data/tenant';
import {PlatformService} from "../../../app-common/services/native/platform.service";

@Injectable({
  providedIn: 'root'
})
export class TenantService {

  authenticated: boolean = false;
  tenantDto: TenantDto;

  constructor( private server: ApiService,
               private appStateService: AppStateService,
               private appService: AppService,
               private tabbingService: TabbingService,
               private platformService: PlatformService,
               private loggerService: LoggerService) {
    const json = localStorage.getItem('tenantDto');
    if (!!json) {
      this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'TenantDto found in local storage');
      this.tenantDto = JSON.parse(json);
    } else {
      this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'TenantDto not found in local storage');
    }
    this.appStateService.appWindowStateChange.subscribe(async state => {
      if (state.previousValue === AppWindowState.Login && state.currentValue === AppWindowState.Tenant) {
        if (this.tenantDto) {
          await this.connectToTenant(this.tenantDto);
        }
      }
    });
  }

  async connectToTenant(tenantDto: TenantDto) {
    this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'Connecting to tenant');
    if (!!tenantDto) {
      this.tenantDto = tenantDto;
    }
    if (this.authenticated) {
      localStorage.setItem('tenantDto', JSON.stringify(this.tenantDto));
      // this.appStateService.appWindowState = AppWindowState.Loading;
      return;
    }
    try {
      this.server.initializeApplicationClient( tenantDto.apiUrl + 'api/');
      localStorage.setItem('tenantDto', JSON.stringify(this.tenantDto));
      this.appStateService.appWindowState = AppWindowState.Loading;
      this.tabbingService.restoreCachedTabs(this.tenantDto.tenant.id);
      this.authenticated = true;
      const isMainWindow = await this.platformService.isMainWindow;
      if (isMainWindow) {
        const tenantClone: TenantDto = JSON.parse(JSON.stringify(this.tenantDto));
        tenantClone.users = null;
        this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, {
          message: 'Connected to tenant',
          entity: tenantClone
        });
        this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'Environment: ' + EnvironmentType[this.tenantDto.environmentType]);
      }
    }
    catch (e) {
      this.appService.showErrorMessage( 'Company code not found!' );
      this.authenticated = false;
      this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'Company code not found!');
    }
  }

  disconnect() {
    this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'Disconnecting from tenant');
    this.tabbingService.saveTabsToCache(this.tenantDto.tenant.id);
    this.authenticated = false;
    this.tenantDto = null;
    localStorage.removeItem('tenantDto');
    this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TenantService, 'Disconnected from tenant');
  }

  async getMyOrganizations(): Promise<TenantDto[]> {
    return await this.server.tenantApiClient.get<TenantDto[]>(`tenant/my?type=${buildNumber.branch}`);
  }

  async createTenant(request: CreateOrganizationRequest): Promise<Tenant> {
    const response = await this.server.tenantApiClient.postWithPD<CreateOrganizationRequest, Tenant>('tenant/createTenant', request);
    if (response) {
      this.appService.showSuccessMessage('Organization created successfully');
      return response;
    }
  }
}
export enum EnvironmentType {
  Production = 0,
  Canary = 1,
  Sandbox = 2
}
